import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { snappyTween } from './motionUi';
import { startJoiSession, nextJoiNode } from '../joiSessionApi';

/**
 * JOI Session Overlay — показывается после Submission Contract.
 *
 * Управляет полным циклом JOI-сессии:
 * - Dialogue line для заданий и комментариев (тот же стиль что GameDialogueBar)
 * - Виджет ответов для PlayerCondition
 * - Кнопки Done / Disobey для заданий (с таймером)
 * - RP и прогресс
 *
 * @param {{ playerKinkIds: string[], playerRealItemIds: string[], targetRating?: string }} props.contract
 * @param {string} props.activeLang — 'en' | 'ru'
 * @param {(result: { earnedRp: number, completedTasks: string[], refusedTasks: string[] }) => void} props.onFinish
 */
export default function JoiSessionOverlay({ contract, activeLang = 'ru', onFinish }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionState, setSessionState] = useState(null);
  const [node, setNode] = useState(null);
  const [transitioning, setTransitioning] = useState(false);

  // Typewriter
  const [visibleChars, setVisibleChars] = useState(0);
  const [lineComplete, setLineComplete] = useState(false);
  const timerRef = useRef(null);

  // Task timer
  const [taskTimerLeft, setTaskTimerLeft] = useState(0);
  const [taskTimerDone, setTaskTimerDone] = useState(false);
  const taskTimerRef = useRef(null);

  // Стартуем сессию
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await startJoiSession({
          playerKinkIds: contract.playerKinkIds || [],
          playerRealItemIds: contract.playerRealItemIds || [],
          targetRating: contract.targetRating || 'Normal'
        });
        if (cancelled) return;
        setSessionState(result.sessionState);
        setNode(result.node);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [contract]);

  // Текст текущей строки
  const currentText = useMemo(() => {
    if (!node?.text) return '';
    return (activeLang === 'en' ? node.text.en : node.text.ru) || node.text.en || node.text.ru || '';
  }, [node, activeLang]);

  // Typewriter effect
  useEffect(() => {
    if (!currentText) return;
    setVisibleChars(0);
    setLineComplete(false);
    let i = 0;
    const step = () => {
      i++;
      setVisibleChars(i);
      if (i < currentText.length) {
        timerRef.current = setTimeout(step, 15);
      } else {
        setLineComplete(true);
      }
    };
    timerRef.current = setTimeout(step, 15);
    return () => { clearTimeout(timerRef.current); };
  }, [currentText]);

  // Task timer (для заданий с duration)
  useEffect(() => {
    if (node?.type !== 'task' || !node.duration || node.duration <= 0) {
      setTaskTimerLeft(0);
      setTaskTimerDone(true);
      return;
    }
    setTaskTimerLeft(node.duration);
    setTaskTimerDone(false);
    const interval = setInterval(() => {
      setTaskTimerLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setTaskTimerDone(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    taskTimerRef.current = interval;
    return () => clearInterval(interval);
  }, [node]);

  const skipTypewriter = useCallback(() => {
    clearTimeout(timerRef.current);
    setVisibleChars(currentText.length);
    setLineComplete(true);
  }, [currentText]);

  // Следующий узел
  const advance = useCallback(async (playerAction) => {
    if (!sessionState || transitioning) return;
    setTransitioning(true);
    try {
      const result = await nextJoiNode({ sessionState, playerAction });
      setSessionState(result.sessionState);
      setNode(result.node);
    } catch (e) {
      setError(e.message);
    }
    setTransitioning(false);
  }, [sessionState, transitioning]);

  // Клик по тексту — скип typewriter или продолжить
  const handleTextClick = useCallback(() => {
    if (!lineComplete) {
      skipTypewriter();
      return;
    }
    // Для текстовых узлов (dialogue, comment, disobey) — продолжить
    if (node?.type === 'dialogue' || node?.type === 'comment' || node?.type === 'disobey') {
      advance();
    }
  }, [lineComplete, node, advance, skipTypewriter]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleTextClick();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleTextClick]);

  // Конец сессии
  useEffect(() => {
    if (node?.type === 'end') {
      onFinish?.({
        earnedRp: node.earnedRp || 0,
        completedTasks: node.completedTasks || [],
        refusedTasks: node.refusedTasks || []
      });
    }
  }, [node, onFinish]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/90">
        <p className="animate-pulse text-sm text-game-muted">Starting JOI session...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-[65] flex flex-col items-center justify-center gap-4 bg-black/90">
        <p className="text-sm text-red-400">{error}</p>
        <button
          onClick={() => onFinish?.({ earnedRp: 0, completedTasks: [], refusedTasks: [] })}
          className="rounded-xl border border-white/15 bg-game-surface px-4 py-2 text-xs text-game-text"
        >
          Close
        </button>
      </div>
    );
  }

  if (!node || node.type === 'end') return null;

  const isTask = node.type === 'task';
  const isCondition = node.type === 'condition';
  const isTextNode = node.type === 'dialogue' || node.type === 'comment' || node.type === 'disobey';
  const canContinue = lineComplete && isTextNode;

  return (
    <div className="fixed inset-0 z-[65] flex flex-col justify-end bg-black/60">
      {/* Dialogue area */}
      <div className="w-full border-t border-white/10 bg-gradient-to-t from-game-canvas via-game-canvas/95 to-transparent px-4 py-4">
        <div
          className={`mx-auto max-w-3xl rounded-xl border border-white/10 bg-game-tint px-4 py-3 ${
            canContinue ? 'cursor-pointer select-none' : ''
          }`}
          onClick={canContinue || !lineComplete ? handleTextClick : undefined}
          role={canContinue ? 'button' : undefined}
          tabIndex={canContinue ? 0 : undefined}
        >
          {/* Speaker label */}
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-game-muted">
            {node.type === 'disobey' ? 'Mistress' : node.commentType === 'PlayerCondition' || isCondition ? 'Mistress' : 'Mistress'}
          </p>

          {/* Text with typewriter */}
          <p className="min-h-[4.5rem] text-left text-base leading-relaxed text-game-text/95">
            {currentText.split('').map((ch, i) => (
              <span key={i} className={i < visibleChars ? '' : 'text-transparent'}>{ch}</span>
            ))}
          </p>
        </div>

        {/* Action buttons */}
        <AnimatePresence mode="wait">
          {/* Task: Done / Disobey */}
          {isTask && lineComplete ? (
            <motion.div
              key="task-actions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="mx-auto mt-3 flex max-w-3xl items-center justify-between gap-3"
            >
              {/* Done button (left) */}
              <motion.button
                type="button"
                transition={snappyTween}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={!taskTimerDone || transitioning}
                onClick={() => advance({ type: 'done' })}
                className={`flex-1 rounded-xl py-3 text-sm font-bold transition-colors ${
                  taskTimerDone
                    ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                    : 'border border-white/15 bg-game-surface text-game-muted'
                }`}
              >
                {taskTimerDone ? (
                  'Done'
                ) : (
                  <span className="flex items-center justify-center gap-1.5">
                    <ClockIcon />
                    <span className="tabular-nums">{taskTimerLeft} sec</span>
                  </span>
                )}
              </motion.button>

              {/* Disobey button (right) */}
              <motion.button
                type="button"
                transition={snappyTween}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={transitioning}
                onClick={() => advance({ type: 'disobey' })}
                className="flex-1 rounded-xl border border-red-500/40 bg-red-500/15 py-3 text-sm font-bold text-red-300 transition-colors hover:bg-red-500/25"
              >
                Disobey
              </motion.button>
            </motion.div>
          ) : null}

          {/* Condition: 4 choice buttons */}
          {isCondition && lineComplete ? (
            <motion.div
              key="condition-actions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="mx-auto mt-3 grid max-w-3xl grid-cols-2 gap-2"
            >
              {(node.options || []).map((opt, i) => (
                <motion.button
                  key={opt.key}
                  type="button"
                  transition={snappyTween}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={transitioning}
                  onClick={() => advance({ type: 'condition', value: opt.key })}
                  className="rounded-xl border border-white/15 bg-game-surface px-3 py-2.5 text-xs font-semibold text-game-text transition-colors hover:border-game-accent/40 hover:bg-game-accent/10"
                >
                  {activeLang === 'en' ? opt.label.en : opt.label.ru}
                </motion.button>
              ))}
            </motion.div>
          ) : null}

          {/* Continue indicator */}
          {canContinue ? (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-2 text-center text-[10px] text-game-muted"
            >
              Click or press Enter to continue
            </motion.p>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}

function ClockIcon() {
  return (
    <svg
      className="h-3.5 w-3.5"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 4.5V8l2.5 1.5" />
    </svg>
  );
}
